/**
 * Free Gift With Purchase — Auto-Add + Auto-Apply Discount Code
 *
 * Once cart subtotal (excluding the gift itself) crosses the
 * threshold: adds the gift product (quantity capped at 1), and
 * applies a discount CODE that makes it $0.00 — via Shopify's real
 * /discount/{code} endpoint, which associates a code with the
 * customer's checkout session server-side. Reverses the gift line
 * when the cart drops back below the threshold.
 *
 * SETUP:
 * 1. In Shopify admin: Discounts -> Create discount -> Amount off
 *    products -> Discount code (NOT automatic). 100% off, applies
 *    to just this one gift product, no minimum requirements.
 *    Use the exact code you set in GIFT_DISCOUNT_CODE below.
 * 2. Save this file as assets/free-gift-cart.js
 * 3. Add before </body>:
 *      <script src="{{ 'free-gift-cart.js' | asset_url }}" defer></script>
 * 4. Set GIFT_VARIANT_IDS, GIFT_DISCOUNT_CODE, and THRESHOLD_CENTS below.
 *
 * NOTE: Shopify carts hold only ONE discount code at a time. If you
 * ever introduce customer-facing discount codes elsewhere on the
 * site, this would need to be revisited so they don't conflict.
 *
 * HISTORY OF FIXES, so future edits don't reintroduce either bug:
 * - v1 tried to apply the code by sending a `discount` field to
 *   /cart/update.js. That is NOT a real parameter that endpoint
 *   supports — it silently did nothing, so the gift line got added
 *   but never actually discounted. Fixed by switching to the real
 *   /discount/{code} endpoint below.
 * - v2 (after fixing the add) re-checked whether the discount had
 *   "taken" by reading final_line_price off /cart.js on every pass,
 *   and reloaded again whenever it looked like it hadn't — this
 *   spun the cart page in an endless reload loop. Fixed by only
 *   applying + reloading once per qualifying cart state (tracked in
 *   sessionStorage, so it survives the reload) instead of re-
 *   verifying on every check.
 */

(function () {
  // PROMO ON/OFF SWITCH
  // Set to false to turn this whole promo off between campaigns —
  // nothing runs, no gift gets added, no discount gets applied, and
  // the script exits immediately on page load. Flip it back to true
  // and refresh to turn it on again. Everything else below (variant
  // ID, discount code, threshold) stays configured and ready to go,
  // so re-enabling is a one-line change next time you run this promo.
  var PROMO_ENABLED = false;
  if (!PROMO_ENABLED) return;

  if (window.__freeGiftCartScriptActive) return;
  window.__freeGiftCartScriptActive = true;

  var GIFT_VARIANT_IDS = [43774496145475]; // <-- single gift variant ID
  var GIFT_DISCOUNT_CODE = "Special FREE GIFT"; // <-- must match the discount code created in admin, exactly
  var THRESHOLD_CENTS = 10000; // $100.00
  var MARKER = "_free_gift_auto"; // present in every request body WE make, so our own fetch/XHR patches skip re-triggering on themselves

  var isChecking = false;
  var pendingRecheck = false;
  var unavailableUntil = {};
  var VARIANT_COOLDOWN_MS = 5 * 60 * 1000;

  // Remembers "discount already applied for a qualifying cart" across
  // reloads, so we don't reapply + reload every single check just
  // because /cart.js doesn't clearly reflect a discount CODE.
  var APPLIED_FLAG_KEY = "freeGiftDiscountAppliedAt";
  // If the flag is older than this, treat it as stale and re-verify
  // once (handles edge cases like the code being removed in admin).
  var APPLIED_FLAG_MAX_AGE_MS = 30 * 60 * 1000;

  // Hard safety brake: never auto-reload more than once per few seconds,
  // no matter what else goes wrong.
  var RELOAD_GUARD_KEY = "freeGiftReloadGuardAt";
  var RELOAD_GUARD_MS = 4000;

  function markDiscountApplied() {
    try {
      sessionStorage.setItem(APPLIED_FLAG_KEY, String(Date.now()));
    } catch (e) {}
  }

  function clearDiscountAppliedFlag() {
    try {
      sessionStorage.removeItem(APPLIED_FLAG_KEY);
    } catch (e) {}
  }

  function discountRecentlyApplied() {
    try {
      var at = Number(sessionStorage.getItem(APPLIED_FLAG_KEY) || 0);
      return at > 0 && Date.now() - at < APPLIED_FLAG_MAX_AGE_MS;
    } catch (e) {
      return false;
    }
  }

  async function getCart() {
    var res = await fetch("/cart.js", { headers: { Accept: "application/json" }, cache: "no-store" });
    return res.json();
  }

  function isGiftLine(item) {
    return GIFT_VARIANT_IDS.indexOf(item.variant_id) !== -1;
  }

  function nonGiftSubtotal(cart) {
    return cart.items.reduce(function (sum, item) {
      return isGiftLine(item) ? sum : sum + item.line_price;
    }, 0);
  }

  async function postCartUpdate(body) {
    body.attributes = Object.assign({}, body.attributes, { _marker: MARKER }); // tags this as our own call
    return fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    });
  }

  async function addOneGift(id) {
    var res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: id, quantity: 1, properties: { _free_gift_auto: "true" } }],
      }),
    });

    if (!res.ok) {
      var errBody = await res.json().catch(function () {
        return null;
      });
      console.warn("Gift variant " + id + " could not be added (likely out of stock):", res.status, errBody);
      unavailableUntil[id] = Date.now() + VARIANT_COOLDOWN_MS;
      return false;
    }
    return true;
  }

  async function capGiftQuantities(cart) {
    var overQuantityLines = cart.items.filter(function (item) {
      return isGiftLine(item) && item.quantity > 1;
    });
    if (!overQuantityLines.length) return false;

    var updates = {};
    overQuantityLines.forEach(function (line) {
      updates[line.key] = 1;
    });
    await postCartUpdate({ updates: updates });
    return true;
  }

  async function addMissingGifts(cart) {
    var presentIds = cart.items.filter(isGiftLine).map(function (i) {
      return i.variant_id;
    });
    var now = Date.now();
    var toTry = GIFT_VARIANT_IDS.filter(function (id) {
      return presentIds.indexOf(id) === -1 && (!unavailableUntil[id] || unavailableUntil[id] < now);
    });
    if (!toTry.length) return false;

    var addedAny = false;
    for (var i = 0; i < toTry.length; i++) {
      var ok = await addOneGift(toTry[i]);
      if (ok) addedAny = true;
    }
    return addedAny;
  }

  async function removeGiftsAndDiscount(cart) {
    var giftLines = cart.items.filter(isGiftLine);
    var updates = {};
    giftLines.forEach(function (line) {
      updates[line.key] = 0;
    });
    await postCartUpdate({ updates: updates });
    // NOTE: there is no verified Ajax endpoint to explicitly detach a
    // discount code from the checkout session once it's been applied
    // via /discount/{code}. We rely instead on the discount being
    // scoped ONLY to the gift product in admin: once that product's
    // line item is gone, the code has nothing left to discount, so
    // leaving it "applied" but inert is harmless. If you ever see it
    // affecting an unrelated order, that's the thing to revisit.
    clearDiscountAppliedFlag();
  }

  // Applies the discount code the real way: Shopify's /discount/{code}
  // endpoint associates the code with the customer's checkout session
  // server-side. (An earlier version of this script tried to pass a
  // "discount" field to /cart/update.js — that is NOT a parameter that
  // endpoint supports, so it silently did nothing. This is the fix.)
  async function applyGiftDiscount() {
    var redirectTo = window.location.pathname || "/cart";
    var url = "/discount/" + encodeURIComponent(GIFT_DISCOUNT_CODE) + "?redirect=" + encodeURIComponent(redirectTo);
    try {
      // redirect: "manual" so we don't actually follow/load the
      // redirected page — we only need the server to process the
      // discount association, which happens before it issues the
      // redirect response.
      await fetch(url, { method: "GET", redirect: "manual", credentials: "same-origin" });
    } catch (e) {
      console.error("Free gift: discount code request failed", e);
    }
    markDiscountApplied();
  }

  function canAutoReload() {
    var last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    return Date.now() - last > RELOAD_GUARD_MS;
  }

  function refreshCartUI() {
    if (window.location.pathname === "/cart") {
      if (!canAutoReload()) {
        console.warn("Free gift: skipping reload, one just happened — avoiding a reload loop.");
        return;
      }
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent("cart:refresh"));
  }

  async function checkAndSyncGifts() {
    if (isChecking) {
      pendingRecheck = true;
      return;
    }
    isChecking = true;
    try {
      var cart = await getCart();
      var qualifies = nonGiftSubtotal(cart) >= THRESHOLD_CENTS;
      var hasAnyGiftLine = cart.items.some(isGiftLine);
      var allGiftsPresent = GIFT_VARIANT_IDS.every(function (id) {
        return cart.items.some(function (item) {
          return item.variant_id === id;
        });
      });

      if (qualifies && !allGiftsPresent) {
        // Fresh qualification: add the gift, apply the discount once,
        // remember we did, and reload to show it.
        var added = await addMissingGifts(cart);
        if (added) {
          await applyGiftDiscount();
          refreshCartUI();
        }
      } else if (!qualifies && hasAnyGiftLine) {
        // Dropped below threshold: remove gift + discount, clear the
        // "applied" memory so it re-applies cleanly next time.
        await removeGiftsAndDiscount(cart);
        refreshCartUI();
      } else if (qualifies && allGiftsPresent) {
        // Steady state: gift is present and cart still qualifies.
        // Only touch anything if the quantity drifted, or if we have
        // no record of ever having applied the discount for this
        // cart (e.g. first load after the gift was added by another
        // path, or the sessionStorage flag expired/was cleared).
        var capped = await capGiftQuantities(cart);
        if (!discountRecentlyApplied()) {
          await applyGiftDiscount();
          refreshCartUI();
        } else if (capped) {
          refreshCartUI();
        }
        // If the discount was already applied and quantity is fine,
        // do nothing — no reload, no re-check. This is what stops
        // the infinite spin: we stop trusting an unreliable "is it
        // free yet" signal from /cart.js as a reason to act again.
      }
    } catch (err) {
      console.error("Free gift sync error:", err);
    } finally {
      isChecking = false;
      if (pendingRecheck) {
        pendingRecheck = false;
        checkAndSyncGifts();
      }
    }
  }

  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : input.url;
    var isOurGiftCall = init && init.body && String(init.body).indexOf(MARKER) !== -1;
    var isCartCall = /\/cart\/(add|change|update|clear)/.test(url);

    return originalFetch.apply(this, arguments).then(function (response) {
      if (isCartCall && !isOurGiftCall) {
        setTimeout(checkAndSyncGifts, 50);
      }
      return response;
    });
  };

  var originalOpen = XMLHttpRequest.prototype.open;
  var originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__isCartCall = /\/cart\/(add|change|update|clear)/.test(url);
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    var isOurGiftCall = body && String(body).indexOf(MARKER) !== -1;
    if (this.__isCartCall && !isOurGiftCall) {
      this.addEventListener("loadend", function () {
        setTimeout(checkAndSyncGifts, 50);
      });
    }
    return originalSend.apply(this, arguments);
  };

  document.addEventListener("DOMContentLoaded", checkAndSyncGifts);
})();